
// Ét hovermål ad gangen (ren logik uden THREE/DOM; testet i hoverState.test.mjs). Alle label-visninger ved hover læser kun dette mål.
//
// Kilder melder ind: vertex-kugler og indsæt-håndtag (Potrees InputHandler), kantog objekt-test (DigitizationModule, højst én pr. frame),
// kopiikonet ("hold": hører til det aktuelle mål). Rangorden ved uenighed: vertex > indsæt-håndtag > kant > objekt.
//
// Stabilisering (kalibreringsværdier i HOVER_RULES):
//   - intet aktuelt mål: det nye vises straks
//   - højere rang end det aktuelle: straks; samme/lavere rang: skift først, når det nye er > switchDistance px nærmere
//     eller har været det nærmeste i switchDelay ms (ingen mærkbar træghed ved et enkelt mål)
//   - forlades målet, bliver det stående linger ms (musen kan nå labelens kopiikon); "hold" forlænger, så længe musen er over ikonet
//   - lærredets mouseleave, vinduet mister fokus, værktøjsskift, valgskift, Escape og start af træk rydder (clear)
//   - under træk er målet frosset til det trukne punkt

export const HOVER_RULES = Object.freeze({
	linger: 150, // ms et forladt mål bliver stående
	switchDelay: 40, // ms et nyt mål af samme/lavere rang skal have været nærmest, før vi skifter
	switchDistance: 6, // px et nyt mål skal være nærmere end det aktuelle for at vinde straks
});

const RANK = {vertex: 4, handle: 3, segment: 2, object: 1};

let target = null; // {kind, visual, index, dist}
let frozen = false;
let leftAt = 0; // 0 = musen er stadig på målet
let holding = 0;
let candidate = null; // {kind, visual, index, dist, since}
let onChange = null;
let version = 0;

export function setHoverChangeListener(fn){
	onChange = typeof fn === "function" ? fn : null;
}

function changed(){
	version++;
	if(onChange) onChange();
}

function same(t, kind, visual, index){
	return !!t && t.kind === kind && t.visual === visual && t.index === index;
}

function setTarget(kind, visual, index, dist){
	target = {kind, visual, index, dist};
	leftAt = 0;
	candidate = null;
	changed();
}

// Musen er over (kind, visual, index); dist = afstand i px fra musen (0 hvis den rammer målet direkte)
export function hoverEnter(kind, visual, index, now, dist = 0){
	if(frozen) return;
	if(same(target, kind, visual, index)){
		leftAt = 0;
		target.dist = dist;
		candidate = null;
		return;
	}
	if(!target){
		setTarget(kind, visual, index, dist);
		return;
	}
	// det aktuelle mål er forladt (ligger i linger): et nyt mål overtager straks
	if(leftAt > 0 && holding === 0){
		setTarget(kind, visual, index, dist);
		return;
	}
	if(RANK[kind] > RANK[target.kind]){
		setTarget(kind, visual, index, dist);
		return;
	}
	if(RANK[kind] < RANK[target.kind]) return;
	if(dist + HOVER_RULES.switchDistance < target.dist){
		setTarget(kind, visual, index, dist);
		return;
	}
	if(!same(candidate, kind, visual, index)){
		candidate = {kind, visual, index, dist, since: now};
		return;
	}
	candidate.dist = dist;
	if(now - candidate.since >= HOVER_RULES.switchDelay) setTarget(kind, visual, index, dist);
}

// Musen forlod (kind, visual, index) - målet bliver stående linger ms
export function hoverLeave(kind, visual, index, now){
	if(frozen) return;
	if(same(candidate, kind, visual, index)) candidate = null;
	if(same(target, kind, visual, index) && leftAt === 0) leftAt = now;
}

// Kopiikonet hører til det aktuelle mål: mens musen er over det, bevares målet
export function hoverHold(){
	holding++;
}

export function hoverRelease(now){
	if(holding > 0) holding--;
	if(holding === 0 && target) leftAt = now; // lingeren starter forfra, når musen forlader ikonet
}

// Kaldes hver frame: udløber et forladt mål. Returnerer true, hvis målet blev ryddet.
export function hoverTick(now){
	if(target && !frozen && leftAt > 0 && holding === 0 && now - leftAt >= HOVER_RULES.linger){
		clearHover("linger");
		return true;
	}
	return false;
}

export function clearHover(reason){
	const had = !!target;
	target = null;
	candidate = null;
	leftAt = 0;
	holding = 0;
	frozen = false;
	if(had) changed();
	return reason;
}

// Under træk af et punkt: målet fastholdes på det trukne punkt og ændres ikke af andre kilder
export function freezeHover(visual, index){
	target = {kind: "vertex", visual, index, dist: 0};
	frozen = true;
	leftAt = 0;
	candidate = null;
	changed();
}

export function unfreezeHover(now){
	if(!frozen) return;
	frozen = false;
	leftAt = now; // lingrer kort efter slip, så ingen label forsvinder ryk-vist
	changed();
}

export function getHoverTarget(){
	return target;
}

export function getHoverVersion(){
	return version;
}

export function isHoverFrozen(){
	return frozen;
}

// Er der et mål, som kommer fra et direkte træf (vertex/håndtag)? Kant-/objekttest springes så over.
export function hasPrimaryTarget(){
	return !!target && (target.kind === "vertex" || target.kind === "handle") && leftAt === 0;
}

// kun til test
export function _resetHover(){
	target = null; frozen = false; leftAt = 0; holding = 0; candidate = null; version = 0; onChange = null;
}
